import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { ShoppingCart, Trash2, ChevronRight, Truck, Package, ArrowLeft, Plus, Minus, Wallet, Gift, AlertCircle } from 'lucide-react';
import { useAlert } from '../context/AlertContext';
import { useConfirm } from '../context/ConfirmContext';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config/api';

const Cart = () => {
    const [cartItemsByPackage, setCartItemsByPackage] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [allPackages, setAllPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [shipping, setShipping] = useState({
        name: '', address: '', city: '', state: '', zip: '', mobile: ''
    });
    const [checkoutPackage, setCheckoutPackage] = useState(null);
    const { showAlert } = useAlert();
    const { showConfirm } = useConfirm();
    const navigate = useNavigate();

    useEffect(() => {
        fetchAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchAllData = async () => {
        try {
            const token = localStorage.getItem('authToken');

            // Fetch all cart items (no filter)
            const cartResponse = await fetch(`${API_BASE_URL}/shop/cart.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const cartData = await cartResponse.json();

            // Fetch all packages
            const packagesResponse = await fetch(`${API_BASE_URL}/shop/products.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const packagesData = await packagesResponse.json();

            if (packagesData.success) {
                setAllPackages(packagesData.package_tabs || []);
            }

            if (cartData.success) {
                // Group cart items by user_package_id
                const grouped = {};
                (cartData.data || []).forEach(item => {
                    const pkgId = item.user_package_id;
                    if (!grouped[pkgId]) {
                        grouped[pkgId] = [];
                    }
                    grouped[pkgId].push(item);
                });

                // Convert to array with package info
                const groupedArray = Object.keys(grouped).map(pkgId => {
                    const pkgInfo = (packagesData.package_tabs || []).find(p => p.user_package_id.toString() === pkgId);
                    return {
                        user_package_id: pkgId,
                        package_name: pkgInfo?.package_name || 'Unknown Package',
                        remaining_balance: pkgInfo?.remaining_balance || 0,
                        items: grouped[pkgId]
                    };
                });

                setCartItemsByPackage(groupedArray);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const removeItem = async (id) => {
        const confirmed = await showConfirm('Remove Item', 'Are you sure you want to remove this item from your cart?', 'warning');
        if (!confirmed) return;
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/shop/cart.php?id=${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                showAlert('Item removed', 'success');
                fetchAllData();
            }
        } catch (error) {
            showAlert('Failed to remove item', 'error');
        }
    };

    const updateQuantity = async (id, currentQty, change) => {
        const newQty = currentQty + change;
        if (newQty <= 0) {
            removeItem(id);
            return;
        }
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/shop/cart.php`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id, quantity: newQty })
            });
            const data = await response.json();
            if (data.success) {
                fetchAllData();
            } else {
                showAlert(data.message, 'error');
            }
        } catch (error) {
            showAlert('Failed to update quantity', 'error');
        }
    };

    const handleCheckout = async (e, userPackageId) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/shop/place_order.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...shipping,
                    user_package_id: userPackageId
                })
            });
            const data = await response.json();
            if (data.success) {
                showAlert('Order placed successfully!', 'success');
                setCheckoutPackage(null);
                setShipping({ name: '', address: '', city: '', state: '', zip: '', mobile: '' });
                fetchAllData();
            } else {
                showAlert(data.message, 'error');
            }
        } catch (error) {
            showAlert('Failed to place order', 'error');
        }
    };

    const getPackageTotal = (items) => {
        return items.reduce((acc, item) => acc + (parseFloat(item.price) * parseInt(item.quantity)), 0);
    };

    const getPackagePV = (items) => {
        return items.reduce((acc, item) => acc + (parseFloat(item.pv_points) * parseInt(item.quantity)), 0);
    };

    const grandTotal = cartItemsByPackage.reduce((acc, pkg) => acc + getPackageTotal(pkg.items), 0);
    const grandPV = cartItemsByPackage.reduce((acc, pkg) => acc + getPackagePV(pkg.items), 0);
    const totalItems = cartItemsByPackage.reduce((acc, pkg) => acc + pkg.items.reduce((a, i) => a + parseInt(i.quantity), 0), 0);
    const totalBonusItems = cartItemsByPackage.reduce((acc, pkg) =>
        acc + pkg.items.reduce((a, i) => {
            const buy = parseInt(i.buy_quantity || 0);
            const free = parseInt(i.get_free_quantity || 0);
            return a + (buy > 0 ? Math.floor(i.quantity / buy) * free : 0);
        }, 0), 0);

    return (
        <Layout>
            <div className="p-4 sm:p-6 max-w-7xl mx-auto">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
                    <h1 className="text-2xl sm:text-3xl font-black text-gray-900 flex items-center gap-3">
                        <ShoppingCart className="text-blue-600" size={28} />
                        Shopping Cart
                    </h1>
                    <button
                        onClick={() => navigate('/shop')}
                        className="flex items-center gap-2 text-gray-500 font-bold hover:text-blue-600 transition-colors uppercase tracking-widest text-xs"
                    >
                        <ArrowLeft size={16} /> Back to Shop
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center p-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
                ) : totalItems > 0 ? (
                    <div className="space-y-6">
                        {/* Grand Summary Card */}
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-5 rounded-2xl text-white shadow-xl">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-widest text-blue-100 mb-1">Cart Summary</p>
                                    <p className="text-2xl font-black">
                                        {totalItems} Units {totalBonusItems > 0 && `+ ${totalBonusItems} FREE GIFTS`}
                                    </p>
                                    <p className="text-[10px] font-bold text-blue-100/60 uppercase tracking-widest mt-1">Across {cartItemsByPackage.length} Package Pooling Groups</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs font-bold uppercase tracking-widest text-blue-100 mb-1">Grand Total</p>
                                    <p className="text-3xl font-black">₹{grandTotal.toLocaleString()}</p>
                                    <p className="text-sm text-blue-100 font-bold">{grandPV.toLocaleString()} PV Points</p>
                                </div>
                            </div>
                        </div>

                        {/* Package-wise Cart Items */}
                        {cartItemsByPackage.map(pkg => {
                            const pkgTotal = getPackageTotal(pkg.items);
                            const pkgPV = getPackagePV(pkg.items);
                            const balance = parseFloat(pkg.remaining_balance);
                            const isCheckingOut = checkoutPackage === pkg.user_package_id;
                            const canCheckout = balance >= pkgTotal;

                            return (
                                <div key={pkg.user_package_id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6">
                                    {/* Package Header */}
                                    <div className="p-4 sm:p-5 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2.5 bg-blue-100 rounded-xl text-blue-600">
                                                <Package size={20} />
                                            </div>
                                            <div>
                                                <h3 className="font-black text-gray-900 uppercase tracking-tight">{pkg.package_name}</h3>
                                                <p className="text-xs text-gray-400 font-bold">{pkg.items.length} item{pkg.items.length > 1 ? 's' : ''}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg border border-green-100">
                                                <Wallet size={14} className="text-green-600" />
                                                <span className="text-sm font-black text-green-700">₹{balance.toLocaleString()}</span>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-lg font-black text-gray-900">₹{pkgTotal.toLocaleString()}</p>
                                                <p className="text-[10px] text-blue-600 font-bold">{pkgPV} PV</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Items */}
                                    <div className="p-4 space-y-3">
                                        {pkg.items.map(item => {
                                            const buy = parseInt(item.buy_quantity || 0);
                                            const free = parseInt(item.get_free_quantity || 0);
                                            const freeUnits = buy > 0 ? Math.floor(item.quantity / buy) * free : 0;
                                            const isBogoActive = buy > 0 && free > 0;

                                            return (
                                                <React.Fragment key={item.id}>
                                                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 hover:bg-gray-50 transition-colors border border-gray-100 rounded-xl bg-white shadow-sm mb-3">
                                                        <div className="flex items-center gap-4 flex-1">
                                                            <div className="h-16 w-16 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-200">
                                                                {item.image_url ? (
                                                                    <img src={item.image_url} alt={item.product_name} className="h-full w-full object-cover" />
                                                                ) : (
                                                                    <ShoppingCart className="text-gray-200" size={24} />
                                                                )}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <h4 className="font-bold text-gray-900 text-sm truncate">{item.product_name}</h4>
                                                                <div className="flex items-center gap-2 mt-1">
                                                                    <span className="text-xs text-gray-500">₹{parseFloat(item.price).toLocaleString()}</span>
                                                                    <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded font-bold">{item.pv_points} PV</span>
                                                                </div>
                                                                {isBogoActive && (
                                                                    <span className="mt-2 inline-block bg-amber-100 text-amber-700 text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider border border-amber-200">
                                                                        Offer: BUY {buy} GET {free}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto border-t sm:border-t-0 pt-3 sm:pt-0">
                                                            {/* Quantity Controls */}
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={() => updateQuantity(item.id, parseInt(item.quantity), -1)}
                                                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-600 transition-all border border-transparent shadow-sm"
                                                                >
                                                                    <Minus size={12} />
                                                                </button>
                                                                <span className="w-8 text-center font-black text-gray-900 text-sm">{item.quantity}</span>
                                                                <button
                                                                    onClick={() => updateQuantity(item.id, parseInt(item.quantity), 1)}
                                                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-600 transition-all border border-transparent shadow-sm"
                                                                >
                                                                    <Plus size={12} />
                                                                </button>
                                                            </div>

                                                            <div className="text-right min-w-[100px]">
                                                                <p className="font-black text-gray-900 text-sm sm:text-base">₹{(parseFloat(item.price) * parseInt(item.quantity)).toLocaleString()}</p>
                                                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">Subtotal</p>
                                                            </div>

                                                            <button
                                                                onClick={() => removeItem(item.id)}
                                                                className="text-gray-300 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 transition-all"
                                                            >
                                                                <Trash2 size={18} />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Free Item Bonus Row if eligible */}
                                                    {freeUnits > 0 && (
                                                        <div className="ml-8 sm:ml-20 mr-4 p-2 bg-indigo-50/50 rounded-xl border border-indigo-100/50 flex items-center justify-between gap-4 animate-fadeIn border-dashed mb-4">
                                                            <div className="flex items-center gap-3">
                                                                <div className="p-1.5 bg-indigo-100 rounded-lg text-indigo-600">
                                                                    <Gift size={12} />
                                                                </div>
                                                                <div>
                                                                    <p className="text-xs font-bold text-indigo-900">
                                                                        Bonus: {freeUnits} × {item.free_product_name || item.product_name}
                                                                    </p>
                                                                    <p className="text-[10px] text-indigo-500 font-medium">Free Gift Added</p>
                                                                </div>
                                                            </div>
                                                            <div className="text-right">
                                                                <span className="text-xs font-black text-indigo-600 bg-white px-2 py-0.5 rounded-lg border border-indigo-100 shadow-sm">FREE</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>

                                    {/* Package Footer / Checkout */}
                                    <div className="p-4 bg-gray-50 border-t border-gray-100">
                                        {!canCheckout && (
                                            <div className="mb-3 p-3 bg-red-50 text-red-700 text-xs font-bold rounded-xl border border-red-100 flex items-center gap-2">
                                                <AlertCircle size={14} />
                                                Insufficient balance (₹{balance.toLocaleString()} available, ₹{pkgTotal.toLocaleString()} needed)
                                            </div>
                                        )}

                                        {!isCheckingOut ? (
                                            <button
                                                onClick={() => setCheckoutPackage(pkg.user_package_id)}
                                                disabled={!canCheckout}
                                                className={`w-full py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${canCheckout
                                                    ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-100'
                                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                    }`}
                                            >
                                                Checkout This Package <ChevronRight size={16} />
                                            </button>
                                        ) : (
                                            <div className="animate-fadeIn">
                                                <h4 className="font-bold mb-3 flex items-center gap-2 text-xs text-gray-500 uppercase tracking-widest">
                                                    <Truck size={14} className="text-blue-500" /> Shipping Details
                                                </h4>
                                                <form onSubmit={(e) => handleCheckout(e, pkg.user_package_id)} className="space-y-3">
                                                    <input required placeholder="Full Name" className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                                        value={shipping.name} onChange={e => setShipping({ ...shipping, name: e.target.value })} />
                                                    <input required placeholder="Address" className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                                        value={shipping.address} onChange={e => setShipping({ ...shipping, address: e.target.value })} />
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <input required placeholder="City" className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                                            value={shipping.city} onChange={e => setShipping({ ...shipping, city: e.target.value })} />
                                                        <input required placeholder="State" className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                                            value={shipping.state} onChange={e => setShipping({ ...shipping, state: e.target.value })} />
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <input required placeholder="ZIP Code" className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                                            value={shipping.zip} onChange={e => setShipping({ ...shipping, zip: e.target.value })} />
                                                        <input required placeholder="Mobile" className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                                            value={shipping.mobile} onChange={e => setShipping({ ...shipping, mobile: e.target.value })} />
                                                    </div>
                                                    <div className="flex gap-2 pt-2">
                                                        <button type="button" onClick={() => setCheckoutPackage(null)} className="flex-1 py-2.5 bg-gray-200 text-gray-600 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-gray-300 transition-colors">Cancel</button>
                                                        <button type="submit" className="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-green-700 shadow-lg shadow-green-100 transition-all">
                                                            Place Order
                                                        </button>
                                                    </div>
                                                </form>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-24 bg-white rounded-3xl border border-gray-100 shadow-sm">
                        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <ShoppingCart size={40} className="text-gray-200" />
                        </div>
                        <h3 className="text-xl font-black text-gray-900 uppercase tracking-tight">Your Cart is Empty</h3>
                        <p className="text-gray-400 font-bold text-xs uppercase tracking-widest mt-2">Add items from the shop to get started</p>
                        <button onClick={() => navigate('/shop')} className="mt-6 px-6 py-3 bg-gray-900 text-white rounded-xl font-bold shadow-lg hover:bg-black transition-all text-sm">
                            Browse Shop
                        </button>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default Cart;
